import { computed, inject, Injectable } from '@angular/core';

import {
  CaseParticipant,
  JudgeReviewFilingAdditionalRecipient,
  JudgeReviewMailingAddress,
  OverlayAlignment,
  WatermarkPosition,
} from '@data-contracts';
import { DOCUMENT_RENDERING } from '@shared/constants';
import { AnnotationType } from '@feature/jr/shared/models/annotation.model';
import {
  JudgeReviewStudioAccessOption,
  JudgeReviewStudioOption,
} from '../models/judge-review-studio.view-model';
import {
  actionDistribution,
  actionDocuments,
} from './judge-review-action.utils';
import { findDocumentActionConfig } from './judge-review-config.utils';
import { JudgeReviewDataService } from './judge-review-data.service';
import { DOCUMENT_REVIEW_INCOMPLETE_CODE } from './map-judge-review-confirm.utils';
import {
  mapAccessTypeOptions,
  mapDispositionLabel,
  mapFilingTypeOptions,
  mapJudicialActionOptions,
  mapSignatureInitials,
  mapSignatureName,
  mapStudioHeader,
} from './map-judge-review-studio.utils';

/**
 * `JRD016 FinalizeNotComplete` — the API's submit gate for the finalize step.
 *
 * Like `JRF008` ({@link DOCUMENT_REVIEW_INCOMPLETE_CODE}), this is a submit-time gate that the
 * Continue step resolves: the studio cannot act on it yet, and surfacing it in
 * the "needs attention before issuing" panel would confuse judges who have not
 * pressed Continue yet. It still reaches the Confirmation page through
 * `confirmOutcome.validationErrors`, which is what disables Submit.
 */
const FINALIZE_ONLY_ERROR_CODE = 'JRD016';

/**
 * `JRF006 CustomServiceListRequired`. Server-computed against the saved filing, so it
 * goes stale the moment the judge picks another Distribution in the studio.
 */
export const CUSTOM_LIST_REQUIRED_CODE = 'JRF006';

/**
 * UI models for the Review & Sign studio and the recipients / address pages:
 * header, signature, stamp assets, upload limits, saved recipient collections and
 * the per-document select options. Maps the filing and configuration DTOs held by
 * `JudgeReviewDataService` through the pure `map-judge-review-*` utilities, so
 * components never bind DTO shapes.
 */
@Injectable({
  providedIn: 'root',
})
export class JudgeReviewStudioViewModelService {
  private readonly data = inject(JudgeReviewDataService);
  private readonly filing = this.data.filingDto;
  private readonly config = this.data.configDto;

  /**
   * Validation errors on the working filing as `{ code, message }` (message is the
   * display string): the studio's "items need attention before issuing" list. The
   * code is kept so callers can filter further, e.g. a server error the studio's
   * local state has already outdated.
   *
   * `JRF008` (every document reviewed) and `JRD016` (finalize complete) are
   * withheld. This list is about what blocks the document in front of the judge,
   * and both are submit-time gates that Continue / submit resolve — showing them
   * here would tell every judge, including on a single-document filing, that
   * something is wrong with work they have not finished yet. They still reach the
   * Confirmation page through `confirmOutcome.validationErrors`.
   */
  readonly validationIssues = computed(() =>
    (this.filing()?.validationErrors ?? [])
      .filter(
        error =>
          error.code !== DOCUMENT_REVIEW_INCOMPLETE_CODE &&
          error.code !== FINALIZE_ONLY_ERROR_CODE &&
          !!error.description,
      )
      .map(error => ({
        code: error.code ?? null,
        message: error.description as string,
      })),
  );

  /** Studio header / context (reviewer, court, lead case). Null while loading. */
  readonly studioHeader = computed(() => mapStudioHeader(this.filing()));

  /** Disposition chip for the studio header, e.g. "GRANT · PROPOSED ORDER". */
  readonly dispositionLabel = computed(() =>
    mapDispositionLabel(this.filing(), this.config()),
  );

  /** Security / Access select options (court + case-class filtered). */
  readonly accessTypeOptions = computed<JudgeReviewStudioAccessOption[]>(() =>
    mapAccessTypeOptions(this.config()?.accessTypes),
  );

  /**
   * Active case participants for the custom-recipients picker page.
   * Sourced from `JudgeReviewFilingConfiguration.serviceableParticipants`.
   */
  readonly serviceableParticipants = computed<CaseParticipant[]>(
    () => this.config()?.serviceableParticipants ?? [],
  );

  /**
   * Custom-recipient participant ids last saved to the filing. The recipients
   * page restores its selection from these on every visit.
   */
  readonly savedCustomSelections = computed<string[]>(
    () =>
      actionDistribution(this.filing())?.customServiceListParticipantIds ?? [],
  );

  /** Additional recipients last saved to the filing; restored on revisit. */
  readonly savedAdditionalRecipients = computed<
    JudgeReviewFilingAdditionalRecipient[]
  >(() => actionDistribution(this.filing())?.additionalRecipients ?? []);

  /**
   * Mailing addresses for unrepresented recipients last saved to the filing.
   *
   * All three recipient collections are read from the filing rather than held
   * in memory: leaving the studio clears this service, the recipients and
   * address pages re-resolve the filing on every visit, and every save adopts
   * the server's patched filing.
   */
  readonly savedMailingAddresses = computed<JudgeReviewMailingAddress[]>(
    () => actionDistribution(this.filing())?.mailingAddresses ?? [],
  );

  /**
   * Selected serviceable participants that have no attorney representation
   * (unrepresented). Drives `jrVerifyUnrepresentedGuard` and the address-collection page.
   *
   * Detection: a combination is unrepresented when at least one of its
   * `Representation` entries has `proSe === true`. This matches the API contract
   * (a no-representation combination has a single `Representation` row with
   * `proSe: true` and `attorneyId: null`).
   */
  readonly selectedUnrepresentedParticipants = computed<CaseParticipant[]>(
    () => {
      const selected = new Set(this.savedCustomSelections());
      return this.serviceableParticipants().filter(
        cp =>
          !!cp.caseParticipantUniqueId &&
          selected.has(cp.caseParticipantUniqueId) &&
          (cp.attorneys ?? []).some(a => a.proSe === true),
      );
    },
  );

  /** Signature name (reviewing judge; the current user when a clerk acts). */
  readonly signatureName = computed(() =>
    mapSignatureName(this.filing()?.judgeReviewerName),
  );

  /** Signature initials, e.g. "S.L." */
  readonly signatureInitials = computed(() =>
    mapSignatureInitials(this.filing()?.judgeReviewerName),
  );

  /**
   * Alternate judge display name for the lead case, if one is assigned.
   * Used to populate the "Include alternate judge name" line in comment annotations.
   */
  readonly alternateJudgeName = computed(() => {
    const name = this.filing()?.judgeReviewCases?.[0]?.alternateJudge?.name;
    return name ? mapSignatureName(name) : '';
  });

  /**
   * The integer JCMS filing ID of the filing under review, as a display string.
   * Shown as "Trans. #N" in comment annotation blocks when the judge includes it.
   */
  readonly reviewedTransactionId = computed(() => {
    const id = this.filing()?.reviewedFilingId;
    return id != null ? String(id) : '';
  });

  /**
   * Config-sourced stamp asset URLs, keyed by annotation type (API-WI-2).
   *
   * `judgeBannerUrl` is a 1-hour read SAS URL for the **judge's own banner** — the
   * overlay file uploaded per judge + court + case class in legacy Judge Review
   * setup (`UserJudgeReviewSettings.WatermarkFile`) — so it feeds the `banner`
   * ("Judge Banner") palette item. The `fsx` item stays unsourced until business
   * defines a distinct FSX-banner asset, which also hides it — the same way
   * `seal` and `filed` are hidden while their URLs are null.
   *
   * The banner is a PDF (the API converts an RTF upload to PDF before handing the
   * URL over), so consumers must not assume these URLs are images.
   */
  readonly assetUrls = computed<Partial<Record<AnnotationType, string>>>(() => {
    const config = this.config();
    const urls: Partial<Record<AnnotationType, string>> = {};
    if (config?.judgeBannerUrl) urls.banner = config.judgeBannerUrl;
    if (config?.courtSealUrl) urls.seal = config.courtSealUrl;
    if (config?.filedStampUrl) urls.filed = config.filedStampUrl;
    return urls;
  });

  /**
   * Corner the judge's banner is anchored to. Legacy applies the banner as a
   * corner-anchored overlay rather than letting the judge drag it, so the studio
   * applies it automatically there. Top-right is the legacy default for a judge
   * whose `OverlayAlignment` was never set.
   */
  /**
   * DTO enums surfaced for the studio, mirroring {@link documentOptions}: the
   * container and its template compare against these instead of importing
   * `@data-contracts`, so a swagger regen stops at this boundary.
   */
  readonly overlayAlignments = OverlayAlignment;
  readonly watermarkPositions = WatermarkPosition;

  readonly judgeBannerAlignment = computed<OverlayAlignment>(
    () => this.config()?.judgeBannerAlignment ?? OverlayAlignment.TopRight,
  );

  /** Accepted upload extensions (UPPERCASE, no dot); empty = no court restriction. */
  readonly allowedFileExtensions = computed(
    () => this.config()?.allowedFileExtensions ?? [],
  );

  /** Max per-document size in bytes (0 / undefined = no cap). */
  readonly maxDocumentSizeBytes = computed(
    () => this.config()?.maxDocumentSizeBytes ?? 0,
  );

  /**
   * Maps each document's integer `existingDocumentId` to its DocStore GUID
   * (`judgeReviewDocumentStoreId`). Used by the studio to match an incoming
   * `DocumentReady` push (keyed by `documentStoreDocumentId`) to the active
   * viewer document (keyed by `existingDocumentId`) without naming DTO types
   * in the component.
   *
   * Reacts to `_filing` — entries appear after the PATCH that reserves the
   * upload slot and are refreshed on every `refreshFiling()` call.
   */
  readonly documentStoreIdByExistingId = computed<Map<number, string>>(() => {
    const docs = actionDocuments(this.filing());
    const map = new Map<number, string>();
    for (const doc of docs) {
      if (doc.existingDocumentId != null && doc.judgeReviewDocumentStoreId) {
        map.set(doc.existingDocumentId, doc.judgeReviewDocumentStoreId);
      }
    }
    return map;
  });

  /**
   * Maps each document's integer `existingDocumentId` to its outgoing DocStore
   * info — GUID, ready flags, and DocStore error. Entry exists only when
   * `judgeReviewDocumentStoreId` is set. Fields mirror the filing DTO so the
   * viewer source selection, gate logic, and error display stay in sync with
   * the `_filing` signal without duplicating state.
   *
   * `isFinalReady` is true once the `converted-pdf` rendering (produced at
   * finalize) is ready. Used as the submit gate (JRD016) in FE part 3.
   *
   * Reacts to `_filing` — entries become ready after `refreshFiling()` resolves
   * following a DocumentReady push that sets `isReady: true` on the server.
   */
  readonly outgoingDocInfoByExistingId = computed<
    Map<
      number,
      {
        storeId: string;
        isReady: boolean;
        docStoreError: string | null;
        isFinalReady: boolean;
      }
    >
  >(() => {
    const docs = actionDocuments(this.filing());
    const map = new Map<
      number,
      {
        storeId: string;
        isReady: boolean;
        docStoreError: string | null;
        isFinalReady: boolean;
      }
    >();
    for (const doc of docs) {
      if (doc.existingDocumentId != null && doc.judgeReviewDocumentStoreId) {
        map.set(doc.existingDocumentId, {
          storeId: doc.judgeReviewDocumentStoreId,
          isReady: doc.isReady ?? false,
          docStoreError: doc.docStoreError ?? null,
          isFinalReady: doc.isFinalReady ?? false,
        });
      }
    }
    return map;
  });

  /**
   * Maps the studio's outgoing-source selection to the rendering name used to
   * fetch the document via the by-GUID judge-review endpoint and to match
   * incoming `DocumentReady` pushes.
   *
   * - `'sign'`        → `ORIGINAL_FILER_PDF` — the base copy-on-open rendering.
   * - `'upload'`      → `JUDGE_REVIEW_REPLACE` — produced by a Replace upload.
   * - `'sign-attach'` → `JUDGE_REVIEW_ATTACH`  — produced by an Attach upload.
   *
   * @param source The active outgoing-document source.
   * @returns The rendering name string for the by-GUID endpoint / Slot match.
   */
  renderingNameForSource(source: string): string {
    switch (source) {
      case 'upload':
        return DOCUMENT_RENDERING.JUDGE_REVIEW_REPLACE;
      case 'sign-attach':
        return DOCUMENT_RENDERING.JUDGE_REVIEW_ATTACH;
      case 'sign':
      default:
        return DOCUMENT_RENDERING.ORIGINAL_FILER_PDF;
    }
  }

  /** Document Type select options. Sourced from the root court filing types list (filing-level, not per-document). */
  documentTypeOptions(): JudgeReviewStudioOption[] {
    return mapFilingTypeOptions(this.config()?.courtFilingTypes);
  }

  /**
   * Resolves the default `judgeReviewFilingTypeId` for a document given the judge's selected judicial action.
   * Looks up the document's `judgeReviewUserSettings` entry whose `judicialActionId` matches; if found and
   * the `judgeFilingTypeId` is set (non-empty GUID), returns that. Otherwise falls back to the document's
   * original attorney filing type (`filingTypeId`). Returns null when neither is available.
   */
  defaultFilingTypeIdForAction(
    documentId: number | null,
    judicialActionId: string | null,
  ): string | null {
    const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';
    const docConfig = this.documentActionConfig(documentId);
    const matched = docConfig?.judgeReviewUserSettings?.find(
      s => s.judicialActionId === judicialActionId,
    );
    if (
      matched?.judgeFilingTypeId &&
      matched.judgeFilingTypeId !== GUID_EMPTY
    ) {
      return matched.judgeFilingTypeId;
    }
    return docConfig?.filingTypeId ?? null;
  }

  /** Fallback Document Type label when no options are configured. */
  documentTypeFallbackLabel(documentId: number | null): string {
    return this.documentActionConfig(documentId)?.filingType || 'Document Type';
  }

  /** Status / Action select options for a document. */
  judicialActionOptions(documentId: number | null): JudgeReviewStudioOption[] {
    return mapJudicialActionOptions(
      this.documentActionConfig(documentId)?.judgeReviewUserSettings,
    );
  }

  /** Current security / access display name for a document (studio fallback). */
  accessTypeForDocument(documentId: number | null): string | null | undefined {
    const filing = this.filing();
    return (
      actionDocuments(filing).find(
        document => document.existingDocumentId === documentId,
      ) ??
      filing?.judgeReviewDocuments?.find(
        document => document.existingDocumentId === documentId,
      )
    )?.accessType;
  }

  /** Config-sourced title fallback for a document (title, then filing type). */
  documentTitleFallback(documentId: number | null): string | null {
    const config = this.documentActionConfig(documentId);
    return config?.documentTitle ?? config?.filingType ?? null;
  }

  /** The configuration entry (judicial actions, titles, filing type) for a document. */
  private documentActionConfig(documentId: number | null) {
    return findDocumentActionConfig(this.config(), documentId ?? -1);
  }
}
